import { sessionManager } from "./session.service";
import { getChatbotConfig } from "../utils/utils";

/**
 * APIのベースURL
 */
const BASE_URL =
  import.meta.env.VITE_API_BACK_DOMAIN ||
  "https://ydqa4cpm9m.ap-northeast-1.awsapprunner.com";

/**
 * リクエストオプション
 */
interface RequestOptions {
  method?: "GET" | "POST";
  body?: any;
  signal?: AbortSignal;
  isStream?: boolean;
}

/**
 * APIリクエスト
 * @param endpoint エンドポイント
 * @param options リクエストオプション
 * @returns APIレスポンス
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = "GET", body, signal } = options;
  let requestBody = body;
  if (method === "POST" && body) {
    const globalConfig = getChatbotConfig();
    requestBody = {
      ...globalConfig,
      ...body,
    };
  }
  // エラーレスポンスの内容を取得
  let errorMessage =
    "現在、こちらのチャットボットはご利用いただけません。管理者にお問い合わせください。";
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // セッションキーが存在する場合、Chat-Sessionヘッダーを追加
    const sessionKey = sessionManager.getSessionKey();
    if (sessionKey) {
      headers["Chat-Session"] = sessionKey;
    }
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers,
      /**
       * chatbot-backendのcredentialsをFalseにするため、
       * 下記をコメントアウト
       */
      // credentials: "include",
      body: requestBody ? JSON.stringify(requestBody) : undefined,
      signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        sessionManager.clearSession();
        throw new Error(
          "セッション情報が無効です。ブラウザの再読み込みを行ってください。"
        );
      }
      if (endpoint.endsWith("/chatbot_config/") && response.status === 402) {
        return await response.json();
      }
      //DB崩れた、DBコネクション異常、タイムアウトなど
      if (response.status === 502 || response.status === 504) {
        throw new Error(errorMessage);
      }

      // エラーレスポンスの内容を取得
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          // JSON形式のエラーレスポンス
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error;
        } else {
          // テキスト形式のエラーレスポンス
          const errorText = await response.text();
          // backendはエラーを実装されてない場合、
          if (!errorText && !errorText.includes("DOCTYPE html")) {
            errorMessage = errorText;
          }
        }
      } catch (parseError) {
        // エラーレスポンスの解析に失敗した場合はデフォルトメッセージを使用
        console.warn("Failed to parse error response:", parseError);
      }

      throw new Error(errorMessage);
    }

    if (options.isStream) {
      return response.body as unknown as T;
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }

    return response.body as unknown as T;
  } catch (error) {
    if (error instanceof Error) {
      //App runnerが落ちた、メンテナンス中など
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        throw new Error(errorMessage);
      }
      throw error;
    }
    throw new Error(errorMessage);
  }
}
